import { useDialogManager } from '@patrianna/dialog-manager'
import { PureComponent, createRef } from 'react'
import type { CSSProperties, ReactNode, ComponentType } from 'react'

import HorizontalScrollComponent from './components'

type Props = {
  children: ReactNode
  className?: string
  classNameContainer?: string
  showArrows?: boolean
  scrolledItemDataAttr?: string
  autoScrollTimeInterval?: number
  dialogVisible: boolean
  isScrollAlways?: boolean
  linesAmount?: number
  'data-test'?: string
  gap?: number
  styleContainer?: CSSProperties
  selectedIndex?: number
  autoScrollToSpace?: number
  isScrollSmooth?: boolean
  with3DImage?: boolean
}
type HorizontalScrollState = {
  isScrolled: boolean
}

const withGlobalStateHoc = <P extends Omit<Props, 'dialogVisible'>>(WrappedComponent: ComponentType<Props>) => {
  const displayName = WrappedComponent.displayName || WrappedComponent.name

  const ComponentWithWiredState = (props: P) => {
    const dialogVisible = useDialogManager('getDialogVisibility')

    return <WrappedComponent {...props} dialogVisible={dialogVisible} />
  }

  ComponentWithWiredState.displayName = `withGlobalState(${displayName})`

  return ComponentWithWiredState
}

function getScrollbarWidth() {
  return window.innerWidth - document.documentElement.clientWidth
}

class HorizontalScroll extends PureComponent<Props, HorizontalScrollState> {
  private wrapperRef = createRef<HTMLDivElement>()

  private scrollRef = createRef<HTMLDivElement>()

  private intervalId: ReturnType<typeof setTimeout> | null

  private autoScrollIntervalInstance: ReturnType<typeof setTimeout> | null = null

  static defaultProps = {
    className: '',
    classNameContainer: '',
    showArrows: true,
    gap: 0,
    isScrollSmooth: true,
  }

  componentDidMount() {
    if (document.documentElement.style.setProperty) {
      document.documentElement.style.setProperty('--scroll', `${getScrollbarWidth()}px`)
    }

    if (this.props.selectedIndex !== undefined) {
      this.scrollToSelectedItem(this.props.selectedIndex, this.props.autoScrollToSpace)
    }

    this.intervalId = setInterval(this.resizeListener, 1000)

    setTimeout(() => {
      this.initAutoScroll()
    }, 2000)
  }

  componentWillUnmount() {
    clearTimeout(this.intervalId)
    clearInterval(this.autoScrollIntervalInstance)
  }

  resizeListener = () => {
    const container = this.scrollRef.current

    if (container && container.children) {
      this.checkArrowsVisibility()
    }
  }

  // check if 'prev' or 'next' navigation arrows should be hidden.
  // For example, initially we hide 'prev' arrow because content can't be scrolled to left.
  // when user scroll to the end of list we hide 'next' arrow
  checkArrowsVisibility = () => {
    const container = this.scrollRef.current
    const wrapper = this.wrapperRef.current

    if (!container || !wrapper) {
      return
    }

    // check 'prev' arrow
    if (container.scrollLeft === 0) {
      wrapper.classList.add('hidePrevlArrow')
    } else {
      wrapper.classList.remove('hidePrevlArrow')
    }

    // check 'next' arrow
    if (container.scrollWidth === container.scrollLeft + container.offsetWidth) {
      wrapper.classList.add('hideNextlArrow')
    } else {
      wrapper.classList.remove('hideNextlArrow')
    }
  }

  prevScroll = () => {
    this.scrollTo(this.scrollRef.current, 'left', 500)
  }

  nextScroll = () => {
    this.scrollTo(this.scrollRef.current, 'right', 500)
  }

  scrollTo = (element: HTMLElement, direction: string, distance: number) => {
    element.scrollBy({ top: 0, left: direction === 'left' ? -distance : distance, behavior: 'smooth' })
  }

  scrollItems = (items: HTMLElement[]) => {
    if (this.scrollRef.current) {
      const itemWidth = items[0].offsetWidth + this.props.gap
      const distanceToFirstItem = itemWidth * items.length - itemWidth
      const isOneItem = itemWidth * 2 > this.scrollRef.current.clientWidth
      const indentBeyondBoundaries = this.scrollRef.current.scrollLeft % itemWidth

      if (
        this.scrollRef.current.scrollWidth <=
        this.scrollRef.current.scrollLeft + this.scrollRef.current.offsetWidth
      ) {
        this.scrollTo(this.scrollRef.current, 'left', distanceToFirstItem)
      } else if (isOneItem && indentBeyondBoundaries !== 0) {
        this.scrollTo(this.scrollRef.current, 'right', itemWidth - indentBeyondBoundaries)
      } else {
        this.scrollTo(this.scrollRef.current, 'right', itemWidth)
      }
    }
  }

  autoScrollHandler = () => {
    const items = document.querySelectorAll(`[${this.props.scrolledItemDataAttr}]`)
    if (items?.length) {
      if (!this.props.dialogVisible) {
        // @ts-ignore
        this.scrollItems(items)
      }
      if (this.props.isScrollAlways) {
        // @ts-ignore
        this.scrollItems(items)
      }
    }
  }

  initAutoScroll = () => {
    if (!(this.autoScrollIntervalInstance || !this.props.autoScrollTimeInterval)) {
      this.autoScrollIntervalInstance = setInterval(this.autoScrollHandler, this.props.autoScrollTimeInterval)
    }
  }

  /**
   * Scrolls to the selected item by index.
   * @param {number} index - Index of the selected item.
   */
  scrollToSelectedItem = (index: number, scrollSpace?: number) => {
    if (this.scrollRef.current) {
      const scrollContainer = this.scrollRef.current
      const children = Array.from(scrollContainer.children)
      const selectedItem = children[index] as HTMLElement

      if (selectedItem) {
        const itemOffsetLeft = selectedItem.offsetLeft
        scrollContainer.scrollTo({
          left: scrollSpace ? itemOffsetLeft - scrollSpace : itemOffsetLeft,
          behavior: this.props.isScrollSmooth ? 'smooth' : 'instant',
        })
      }
    }
  }

  render() {
    const { showArrows, className, classNameContainer, children, styleContainer, linesAmount, with3DImage } = this.props

    return (
      <HorizontalScrollComponent
        with3DImage={with3DImage}
        linesAmount={linesAmount}
        wrapperRef={this.wrapperRef}
        scrollRef={this.scrollRef}
        prevScroll={this.prevScroll}
        nextScroll={this.nextScroll}
        className={className}
        classNameContainer={classNameContainer}
        showArrows={showArrows}
        data-test={this.props?.['data-test']}
        styleContainer={styleContainer}
      >
        {children}
      </HorizontalScrollComponent>
    )
  }
}

export default withGlobalStateHoc(HorizontalScroll)
